// Compass Landing Page Data
// Compass — Live Your Values. A habit tracker built around your core values:
// choose what matters, set a direction for each value, back it with daily
// habits, and see how aligned each day is.

const APP_STORE_URL =
  "https://apps.apple.com/us/app/compass-live-your-values/id6756574443";

export const heroData = {
  headline: "More than a habit tracker.",
  subheadline:
    "Compass starts with what matters to you and turns it into small daily habits, so every check-off moves your life somewhere.",
  reassurance: "Free on iPhone and iPad. No account needed.",
  ctaSecondary: "See how it works",
  appStoreUrl: APP_STORE_URL,
};

// Drafted from the developer's brief: "I wanted to create something more than
// just a habit tracking app." Edit freely; it should sound like the developer.
export const makerNote = {
  quote:
    "I didn't want to build another checklist. Most habit apps count what you did. I wanted one that remembers why you're doing it, so a glass of water or a phone-free dinner adds up to the life you actually want.",
  name: "Domonique Dixon",
  role: "Independent developer, Compass",
};

export const screenshots = [
  {
    src: "/images/compass/screenshot-today.png",
    alt: "Compass Today screen: a week strip, the Today's Alignment card showing 0 of 1 action done, and a Drink water habit under Overall Wellness",
    label: "Today",
  },
  {
    src: "/images/compass/screenshot-values.png",
    alt: "Compass screen listing six values, including Overall Wellness, Relationships and Personal Growth, each with its directions and habits",
    label: "Values",
  },
  {
    src: "/images/compass/screenshot-coach.png",
    alt: "The AI Coach asking what you want to move toward, with shortcuts to build a habit, link to a value, plan your week, or get unstuck",
    label: "AI Coach",
  },
  {
    src: "/images/compass/screenshot-progress.png",
    alt: "Compass Progress screen with a plain-language summary of your streak, a weekly bar chart, and the weekly reflection card",
    label: "Progress",
  },
];

// The model that sets Compass apart, shown as one worked example.
export const howItWorksData = [
  {
    step: 1,
    kind: "Value",
    example: "Relationships",
    description: "What matters to you. Pick from a starter set or write your own.",
  },
  {
    step: 2,
    kind: "Direction",
    example: "Be more present at home",
    description: "How you want to live that value, in your own words.",
  },
  {
    step: 3,
    kind: "Habit",
    example: "Phone-free dinner",
    description: "The small thing you actually do today. Check it off, or log it a few times a day.",
  },
];

/**
 * Lead features, each paired with a crop of a real app screenshot.
 * `crop` is the [top, bottom] slice of the image height to show (0–1).
 */
export const spotlightData = [
  {
    id: "today",
    title: "See how aligned today is",
    description:
      "The Today screen counts the actions you've done and tells you which value the next one points to. Tap any day in the week strip to look back at it.",
    points: [
      "Habits grouped under the value they serve",
      "Log a habit once, or a few times a day, until you hit your target",
      "Streaks on every habit, with a celebration when you follow through",
    ],
    image: "/images/compass/screenshot-today.png",
    imageHeight: 1800,
    crop: [0.24, 0.83] as [number, number],
    imageAlt:
      "Today's Alignment card reading 0 of 1 action, one left, pointing to Overall Wellness, above a Drink water habit with a Log one button",
    premium: false,
  },
  {
    id: "ai-coach",
    title: "Tell the AI Coach where you want to go",
    description:
      "Describe what you want to move toward in your own words. The Coach turns it into a value, a direction and the daily habits that get you there, and you add the plan in one tap.",
    points: [
      "Link habits you already have to a value",
      "Plan your week, or ask for a next step when you're stuck",
      "Powered by Claude. Only what you type to the Coach is sent",
    ],
    image: "/images/compass/screenshot-coach.png",
    imageHeight: 1800,
    crop: [0.155, 0.67] as [number, number],
    imageAlt:
      "The AI Coach asking 'What do you want to move toward?' with shortcuts: Build a habit, Link to a value, Plan my week, I'm stuck",
    premium: true,
  },
  {
    id: "progress",
    title: "Look back as well as ahead",
    description:
      "Progress sums up your month in plain language and shows the week at a glance. Close each day with a quick mood and a note, and Premium writes a short summary of your week.",
    points: [
      "Weekly chart, activity heatmap and streak history",
      "Evening reflection with an optional reminder",
      "20 achievement badges, from First Spark to Year Champion",
    ],
    image: "/images/compass/screenshot-progress.png",
    imageHeight: 1795,
    crop: [0.32, 0.745] as [number, number],
    imageAlt:
      "Progress screen: a Your Compass so far summary above a This week bar chart",
    premium: false,
  },
];

export const extrasData = [
  {
    id: "reminders",
    icon: "bell",
    title: "Reminders that help",
    description: "Mark a habit done from the notification and get a heads-up before a streak breaks.",
  },
  {
    id: "scheduling",
    icon: "clock",
    title: "Your schedule",
    description: "Every day or only on the days you choose.",
  },
  {
    id: "accessibility",
    icon: "text",
    title: "Easy to read",
    description: "Light and dark mode, AA contrast, and layouts that hold up at the largest text sizes.",
  },
  {
    id: "cloud-sync",
    icon: "cloud",
    title: "Cloud sync",
    description: "Premium keeps your values, habits and streaks in sync across your devices.",
  },
];

export const pricingData = {
  free: {
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Everything you need to start. Your data stays on your device.",
    features: [
      { text: "Values and directions", included: true },
      { text: "Up to 3 habits", included: true },
      { text: "Today's Alignment and streaks", included: true },
      { text: "Evening reflection", included: true },
      { text: "Reminders", included: true },
      { text: "11 achievement badges", included: true },
      { text: "AI Coach", included: false },
      { text: "Cloud sync", included: false },
    ],
    cta: "Get the app",
  },
  premium: {
    name: "Premium",
    price: "$4.99",
    period: "/month",
    yearlyPrice: "$39.99/year",
    yearlySaving: "save 33%",
    description: "For living every value fully.",
    features: [
      { text: "Everything in Free", included: true },
      { text: "Up to 100 habits", included: true },
      { text: "AI Coach", included: true },
      { text: "AI summary of your week", included: true },
      { text: "Advanced progress stats", included: true },
      { text: "All 20 achievement badges", included: true },
      { text: "Cloud sync and backup", included: true },
    ],
    cta: "Get the app and start your trial",
    reassurance: "Start the 14-day free trial from inside the app. You're billed through the App Store after it ends, and can cancel anytime in your App Store settings.",
  },
};

export const faqData = [
  {
    id: "directions",
    question: "What's the difference between a value and a direction?",
    answer:
      "A value is what matters to you, such as Relationships. A direction is a specific way you want to live it, such as \"Be more present at home.\" Habits are the daily actions that move you in that direction, so every habit you check off points back to a value.",
  },
  {
    id: "without-ai",
    question: "Can I use Compass without the AI?",
    answer:
      "Yes. The AI Coach and the weekly reflection summary are optional Premium features. Values, directions, habits, streaks and reflections all work without them, and nothing is sent to an AI unless you use one of those two features.",
  },
  {
    id: "trial",
    question: "How does the 14-day free trial work?",
    answer:
      "Starting the trial unlocks every Premium feature for 14 days at no cost. You aren't charged until the trial ends, and you can cancel anytime before then in your App Store settings.",
  },
  {
    id: "ai-coach",
    question: "What does the AI Coach do?",
    answer:
      "The AI Coach is a Premium feature powered by Claude. Describe what you want to move toward in plain language and it suggests a value, a direction and habits you can add in one tap. It can also link existing habits to a value, help plan your week, or suggest a next step when you're stuck.",
  },
  {
    id: "reflection",
    question: "How does the evening reflection work?",
    answer:
      "At the end of the day, Compass invites you to pick a mood and write a short note about how it went. Reflections are free and private to you. With Premium, once you've reflected on two or more evenings in a week, the AI writes a short summary of your week on the Progress screen.",
  },
  {
    id: "cloud-sync",
    question: "Does my data sync across devices?",
    answer:
      "Cloud sync and automatic backup are part of Premium. Sign in and your values, habits and streaks sync to every device on your account. On the free plan your data is stored on your device.",
  },
  {
    id: "privacy",
    question: "Is my data private?",
    answer:
      "On the free plan your data stays on your device. With Premium cloud sync it's stored in Google Firebase, encrypted in transit and at rest, and access rules let only your signed-in account read it. It isn't end-to-end encrypted. Your data is never sold. If you use the AI Coach, what you type is sent to Anthropic to write a reply; if you use the weekly summary, that week's reflection notes are. The Privacy Policy has the details.",
  },
  {
    id: "devices",
    question: "What devices are supported?",
    answer:
      "Compass is available for iPhone and iPad. It supports light and dark mode and Dynamic Type, and every screen reflows for the largest accessibility text sizes.",
  },
];

export const ctaData = {
  headline: "Start with one value.",
  subheadline:
    "Pick what matters most, add one small habit, and see how your day lines up.",
  note: "Free on iPhone and iPad. No account needed.",
  qrLabel: "On your computer? Scan to open Compass on your iPhone.",
  appStoreUrl: APP_STORE_URL,
};

export const navigationLinks = [
  { id: "how-it-works", label: "How it works" },
  { id: "features", label: "Features" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
];
